import { ContactDetail } from '@/lib/api';
import type { NameFormat, DateFormatOption } from './types';

export function getDateGroup(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const threeMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 3, 1);

  if (date >= thisMonth) return 'This Month';
  if (date >= lastMonth) return 'Last Month';
  if (date >= threeMonthsAgo) return 'Last 3 Months';
  return 'Earlier';
}

export function groupContactsByDate(contacts: ContactDetail[]): Map<string, ContactDetail[]> {
  const groups = new Map<string, ContactDetail[]>();
  const order = ['This Month', 'Last Month', 'Last 3 Months', 'Earlier'];

  for (const label of order) {
    groups.set(label, []);
  }

  for (const contact of contacts) {
    const group = getDateGroup(contact.gatheredAt);
    groups.get(group)!.push(contact);
  }

  // Remove empty groups
  for (const [key, value] of groups) {
    if (value.length === 0) groups.delete(key);
  }

  return groups;
}

export function formatDate(dateStr: string, format: DateFormatOption = 'us'): string {
  const date = new Date(dateStr);
  switch (format) {
    case 'international':
      return date.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });
    case 'iso':
      return date.toISOString().slice(0, 10);
    case 'us':
    default:
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
  }
}

export function formatContactName(
  contact: { firstName: string; lastName: string },
  format: NameFormat = 'first-last',
): string {
  const first = contact.firstName?.trim() || '';
  const last = contact.lastName?.trim() || '';

  if (!first && !last) return '';
  if (!last) return first;
  if (!first) return last;

  switch (format) {
    case 'last-first':
      return `${last} ${first}`;
    case 'last-comma-first':
      return `${last}, ${first}`;
    case 'first-last':
    default:
      return `${first} ${last}`;
  }
}

const AVATAR_COLORS = [
  '#6366f1',
  '#8b5cf6',
  '#ec4899',
  '#ef4444',
  '#f97316',
  '#eab308',
  '#22c55e',
  '#14b8a6',
  '#06b6d4',
  '#3b82f6',
  '#7c3aed',
  '#db2777',
];

export function getInitials(firstName: string, lastName: string): string {
  const f = firstName?.trim()?.[0]?.toUpperCase() || '';
  const l = lastName?.trim()?.[0]?.toUpperCase() || '';
  return f + l || '?';
}

export function getAvatarColor(firstName: string, lastName: string): string {
  const name = `${firstName}${lastName}`;
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}
